<template>
  <div class="split-section__block-container">
    <component
      :is="element(block)"
      v-for="(block, index) of blocks"
      :id="block.id"
      :key="`${block.id}-${index}`"
      class="split-section__block"
      :class="[
        block.styles,
        block.mobileHeight == 'auto' && 'split-section__block--mobile-auto',
        block.desktopHeight == 'auto' && 'split-section__block--desktop-auto',
        (block.hoverAnimation && block.ctaUrl) && 'split-section__block--hoverable',
      ]"
      :href="block.ctaUrl"
    >
      <art-direction
        block-class="split-section"
        :image="block.mobileImage"
        :image-desktop="block.desktopImage"
        :ratio="blockRatio(block.mobileHeight)"
        :ratio-desktop="blockRatio(block.desktopHeight)"
        :padding="
          isMobile && block.mobileHeight != 'auto' ||
            !isMobile && block.desktopHeight != 'auto'
        "
        @art-direction-loaded="removePlaceholders"
      />

      <div class="split-section__block-content">
        <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
        <img
          v-if="block.icon.src"
          class="split-section__block-icon"
          :src="block.icon.src"
          @load="removePlaceholders"
        >

        <p
          v-if="block.subtitle"
          class="split-section__block-subtitle"
          :class="[
            block.subtitleColour,
            block.subtitleStyles,
          ]"
          v-html="block.subtitle"
        />

        <p
          v-if="block.title"
          class="split-section__block-title"
          :class="[
            block.titleColour,
            block.titleStyles,
          ]"
          v-html="block.title"
        />

        <div
          v-if="block.bodyText"
          class="split-section__block-body"
          :class="[
            block.bodyColour,
            block.bodyStyles,
          ]"
          v-html="block.bodyText"
        />

        <btn
          v-if="block.buttonText && block.ctaUrl"
          block-class="split-section"
          :modifiers="[buttonModifier(block.buttonColour)]"
          :label="block.buttonText"
          :url="block.ctaUrl"
        />
      </div>
    </component>
  </div>
</template>

<script>

/**
 * Vue: Split section (split-section)
 * -----------------------------------------------------------------------------
 * A 50/50 section containing text and media.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { mapState } from 'vuex'

import ArtDirection from '~global/images/art-direction'
import VideoPlayer from '~global/video-player/video-player'
import Btn from '~global/btn/btn'

export default {
  name: 'SplitSection',

  components: {
    ArtDirection,
    VideoPlayer,
    Btn,
  },

  props: {
    blocks: {
      type: Array,
      default: () => [
        {
          id: '',
          animate: false,
          bodyColour: '',
          bodyText: '',
          bodyStyles: '',
          buttonColour: '',
          buttonText: '',
          ctaUrl: '',
          desktopHeight: '',
          desktopImage: '',
          hoverAnimation: false,
          icon: '',
          mobileHeight: '',
          mobileImage: '',
          styles: '',
          subtitle: '',
          subtitleColour: '',
          title: '',
          titleColour: '',
          titleStyles: '',
        },
      ],
    },
    // liquid: {
    //   type: Object,
    //   default: () => ({
    //     sectionId: '',
    //   }),
    // },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  watch: {
    blocks: {
      immediate: true,
      handler() {
        const media = this.doesContentHaveMedia()
        if (media) {
          return
        }

        this.removePlaceholders()
      },
    },
  },

  mounted() {
    for (const block of this.blocks) {
      if (block.animate) {
        gsap.registerPlugin(ScrollTrigger)

        ScrollTrigger.config({
          limitCallbacks: true,
          ignoreMobileResize: true,
        })

        this.initScrollTrigger(block.id)
      }
    }
  },

  methods: {

    /**
     * Indicates if the supplied blocks contain any media content.
     * We use this to manually trigger removePlaceholders() if there are no
     * images that load which trigger the same function on load event.
     * @returns {Boolean}
     */
    doesContentHaveMedia() {
      const media = this.blocks.find((block) => {
        if (block.desktopImage?.src || block.mobileImage?.src) {
          return block
        }

        return false
      })

      return Boolean(media)
    },

    /**
     * Removes liquid placeholders when image content has loaded within the
     * overlapping Vue components.
     */
    removePlaceholders() {
      const parent = this.$el?.closest('section')
      if (!parent) {
        return
      }

      const placeholder = parent.querySelector('.liquid-removed')
      if (!placeholder) {
        return
      }

      placeholder.remove()
    },

    /**
     * Modify element to a link or div.
     */
    element(block) {
      if (block.ctaUrl) {
        return 'a'
      }

      return 'div'
    },

    /**
     * Get button modifier
     */
    buttonModifier(colour) {
      let modifier = ''

      switch (colour) {
        case 'primary':
          modifier = 'primary'
          break

        case 'secondary':
          modifier = 'secondary'
          break

        case 'primary-dark':
          modifier = 'primary-dark'
          break

        case 'secondary-dark':
          modifier = 'secondary-dark'
          break

        case 'primary-grey':
          modifier = 'primary-grey'
          break

        case 'secondary-grey':
          modifier = 'secondary-grey'
          break

        default:
          modifier = 'primary'
          break
      }

      return modifier
    },

    /**
     * Get button modifier
     */
    blockRatio(height) {
      let ratio = ''

      switch (height) {
        case 'square':
          ratio = '1:1'
          break

        case 'portrait':
          ratio = '2:3'
          break

        case 'auto':
        default:
          ratio = ''
          break
      }

      return ratio
    },

    setGsapConfig(element, config) {
      if (!element) {
        return {}
      }

      return gsap.from(element, config)
    },

    /**
     * Initialise scroll-trigger.
     * - Creates a gsap timeline linked to scroll.
     *   - {scrub} tells the animation to be linked to scroll
     */
    initScrollTrigger(id) {
      const container = document.getElementById(id)

      if (!container) {
        return
      }

      gsap.from(container, {
        scrollTrigger: {
          trigger: container,
          scrub: true,
          start: 'top bottom',
          end: 'top center+=5%',
        },
        opacity: 0,
        y: 100,
      })

      const scrollConfig = {
        scrollTrigger: {
          trigger: container.querySelector('.split-section__block-content'),
          scrub: true,
          start: 'top bottom',
          end: 'top center+=5%',
        },
        y: 90,
      }

      const icon = container.querySelector('.split-section__block-icon')
      const subtitle = container.querySelector('.split-section__block-subtitle')
      const title = container.querySelector('.split-section__block-title')
      const body = container.querySelector('.split-section__block-body')
      const button = container.querySelector('.split-section__button')

      this.setGsapConfig(icon, scrollConfig)
      this.setGsapConfig(body, scrollConfig)
      this.setGsapConfig(button, scrollConfig)

      this.setGsapConfig(subtitle, {
        ...scrollConfig,
        marginBottom: '30px',
      })

      this.setGsapConfig(title, {
        ...scrollConfig,
        marginBottom: '60px',
      })
    },
  },
}
</script>

<style lang="scss">
@import './split-section';
</style>
